import React, { useEffect, useReducer, useRef, useState } from "react";

import { ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { Input, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Endpoint from "../../../../services/api/endpoint";
import { getInitialState } from "../../../../state";
import { communicationTemplateReducer } from "../../../../state/reducers";
import { IApplicationState } from "../../../../store";
import { handleChange } from "../../../../utils/editorUtils";
import { ICommunicationTemplate, IUser } from "../../../../utils/types/models";
import { isNullOrEmpty } from "../../../../utils/utils";
import EntityEditor, { IEditorProps, IEditScreenProps, TextArea, YesNoSwitch } from "../../../global/editor";

const CommunicationTemplateEditor: React.FC<IEditorProps<ICommunicationTemplate>> = (props: IEditorProps<ICommunicationTemplate>) => {
	const { t } = useTranslation();
	const [communicationTemplateState, dispatch] = useReducer(communicationTemplateReducer, getInitialState<ICommunicationTemplate>());
	const currentUser: IUser = useSelector((state: IApplicationState) => state.authenticationState.currentUser);
	const initalCommunicationTemplate: ICommunicationTemplate = {
		id: (props as IEditScreenProps<ICommunicationTemplate>).recordId || 0,
		name: "",
		subject: "",
		body: "",
		reminderBody: "",
		active: true,
		companyId: currentUser.currentCompanyId
	};
	const [communicationTemplate, setCommunicationTemplate] = useState(initalCommunicationTemplate);
	const [dataChange, setDataChanged] = useState(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (communicationTemplateState.entity) {
			setCommunicationTemplate(communicationTemplateState.entity);
		}
	}, [communicationTemplateState.entity]);

	function onChange(event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | SwitchChangeEvent | ComboBoxChangeEvent): void {
		setCommunicationTemplate(handleChange(communicationTemplate, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(communicationTemplate.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		if (isNullOrEmpty(communicationTemplate.subject)) {
			messages.push(t("fill_in_required_field", { field: t("subject").toLowerCase() }));
		}
		if (isNullOrEmpty(communicationTemplate.body)) {
			messages.push(t("fill_in_required_field", { field: t("body").toLowerCase() }));
		}
		return messages;
	}

	const readonly: boolean = (props as IEditScreenProps<ICommunicationTemplate>).readonly;
	return (
		<EntityEditor
			width="70%"
			record={communicationTemplate}
			endpoint={Endpoint.CommunicationTemplates}
			entityState={communicationTemplateState}
			entityType={t("template")}
			dispatch={dispatch}
			dataChanged={dataChange}
			readonly={readonly}
			recordName={communicationTemplate.name}
			close={props.close}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input ref={firstField} name="name" className="full-width-field" value={communicationTemplate.name} onChange={onChange} required disabled={readonly} />
				</label>
				<label className="k-form-field">
					<span>{t("subject")} *</span>
					<Input name="subject" className="full-width-field" value={communicationTemplate.subject} onChange={onChange} required disabled={readonly} />
				</label>
				<label className="k-form-field">
					<span>{t("body")} *</span>
					<TextArea name="body" className="full-width-field" value={communicationTemplate.body} onChange={onChange} required />
				</label>
				<label className="k-form-field">
					<span>{t("reminderBody")}</span>
					<TextArea name="reminderBody" className="full-width-field" value={communicationTemplate.reminderBody} onChange={onChange} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<span>{t("active")}</span>
						<YesNoSwitch name="active" checked={communicationTemplate.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default CommunicationTemplateEditor;
